import { trns, range, lowchar } from "./helper.js";

import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';

import { Msgs, KeyStringAny } from "./types/types.js";

export const success = (message?: string | string[]) => ({ status: "success", message, ok: true });
export const error = (message?: string | string[]) => ({ status: "error", message, ok: false });
export const warning = (message?: string | string[]) => ({ status: "warning", message, ok: true });


export const isValidPostalCode = (c: string, p: string) => {
  if (!postcodeValidatorExistsForCountry(c)) {
    return success();
  }
  if (postcodeValidator(p, c)) {
    return success();
  }
  return error(trns("IDS_POSTAL_INVALID", "Your postal/zip code appears to be invalid."));
};

export const validEmail = (email: string) => {
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
  const stat = re.test(email);
  if (stat) {
    return success();
  } else {
    return error(
      trns("IDS_LOGINBOX_EMAIL_INVALID", "Your email address appears to be invalid.")
    );
  }
};

export const isCheckedValidator = (v: boolean) => {
  if (v) return success();
  else return error(trns("IDS_CONFIRM_AGE", "Please confirm you are legal gambling age"));
};

export const termsValidator = (v: boolean) => {
  if (v) return success();
  else
    return error(trns("IDS_AGREE_TERMS_CONFIRM", "You must agree to the Terms & Conditions"));
};

export const validNumber = (num: string) => Number.isNaN(parseInt(num, 10));
// Oddly this is a string, but it's a number validator

export const validLength = (pass: string) => {
  const len = pass.length;
  if (len >= 8) return success();
  else
    return error(
      trns("IDS_LOGINBOX_PASSWORD_MIN_REQ", "Password must be at least 8 characters.\n")
    );
};

export const validPass = (pass: string) => {
  let len = validLength(pass);
  if (len.ok) {
    let uniq: Msgs = validUniqueness(pass);
    let seq: Msgs = validSequence(pass);

    let um: string = uniq.msg as string;
    let sm: string = seq.msg as string;

    if (!uniq.ok && !seq.ok) return error([um, sm]);
    else if (!uniq.ok && seq.ok) return uniq;
    else if (uniq.ok && !seq.ok) return seq;
    return success();
  }
  return len;
};

export const validUniqueness = (pass: string | string[]) => {
  let s = new Set(pass);
  if (s.size > 2) return success();
  else
    return error(
      trns("IDS_LOGINBOX_PASSWORD_UNIQUE", "Must contain more than 3 unique characters.")
    );
};

export const validInputLength = (name: string) => {
  const len = name.trim().length;
  if (len !== 0 && len < 2) {
    return warning(
      trns("IDS_REGISTRATION_ENTIRE_NAME_MANDATORY", "Your entire name is required")
    );
  } else {
    return success();
  }
};

export const validSequence = (pass: string) => {
  let s = new Set(pass);
  let low: number | null = lowchar(pass);
  let simple = low && range(low, low + s.size).map(c => String.fromCharCode(c));

  let simpleStr: string = (simple as string[]).join("");
  let rev = simpleStr
    .split("")
    .reverse()
    .join("");

  if (rev === pass || simpleStr === pass)
    return error(
      trns(
        "IDS_PASSWORD_SIMPLE",
        "Your password is too simple-- add a greater variety of characters."
      )
    );
  else return success();
};

export const validGender = (gender: string) => {
  if (gender === "female" || gender === "male") return success();
  else return error(trns("IDS_FIELD_REQUIRED", "This field is required"));
};

export const validLogin = (login: string) => {
  const len = login.length;
  if (len >= 4) return success();
  else
    return error(
      trns("IDS_LOGINBOX_LOGIN_LENGTH_4", "Username must be between 4 and 12 characters")
    );
};

export const validPhone = (num: string) => {
  const valid = /^[0-9]{5,}/.test(num);
  if (valid) {
    return success();
  } else {
    return error(
      trns("IDS_LOGINBOX_VALID_PHONE", "Numbers only, include your country and area code.")
    );
  }
};

export const validSMSCode = (num: string) => {
  const valid = /[0-9]{4,}/.test(num);
  return valid ? success() : error(trns("IDS_LOGINBOX_VALID_SMS", "Not a valid SMS."));
};

export const idBoundValidator = (array: KeyStringAny, id: number) => {
  let max: number = Number.MIN_SAFE_INTEGER;
  let min: number = Number.MAX_SAFE_INTEGER;

  for (let i: number = 0; i < array.length; i++) {
    max = Math.max(max, array[i].id);
    min = Math.min(min, array[i].id);
  }
  if (id > max || id < min) {
    return error(trns("IDS_FIELD_REQUIRED", "This field is required"));
  } else {
    return success();
  }
};

export const boundedValidator = (array: number[], id: number) => {
  let max: number = Number.MIN_SAFE_INTEGER;
  let min: number = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < array.length; i++) {
    max = Math.max(max, array[i]);
    min = Math.min(min, array[i]);
  }
  if (id > max || id < min) {
    return error(trns("IDS_FIELD_REQUIRED", "This field is required"));
  } else {
    return success();
  }
};

export const indexValidator = (array: number[], id: number) => {
  let max: number = Number.MIN_SAFE_INTEGER;
  let min: number = Number.MAX_SAFE_INTEGER;
  for (let i = 0; i < array.length; i++) {
    max = Math.max(max, i + 1);
    min = Math.min(min, i + 1);
  }
  if (id > max || id < min) {
    return error(trns("IDS_FIELD_REQUIRED", "This field is required"));
  } else {
    return success();
  }
};

export const yearValidator = (year: number) => {
  const current = new Date().getFullYear();
  if (year >= current) {
    return error("Please select a valid year");
  } else if (year <= 1900) {
    return error("Please select a valid year");
  } else {
    return success();
  }
};

export const notEmpty = (value: string | number) => {
  if (value !== null && value.toString().trim() !== "") {
    return success();
  } else {
    return error(trns("IDS_FIELD_REQUIRED", "This field is required"));
  }
};
